import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authQueryKeys } from '@/shared/hooks/queries/queryKeys';

export const LOGOUT_BROADCAST_CHANNEL = 'auth_logout';

/**
 * Safely broadcast logout to other tabs.
 * Gracefully no-ops if BroadcastChannel is unavailable (older browsers, Node.js test environments).
 */
export const broadcastLogout = () => {
  if (typeof BroadcastChannel === 'undefined') {
    return; // Silent no-op — user manually logs out in other tabs
  }

  try {
    const channel = new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL);
    channel.postMessage('logout');
    channel.close();
  } catch {
    // Silently ignore errors (channel creation, postMessage, close)
  }
};

/**
 * Hook to listen for cross-tab logout broadcasts.
 * When a logout event is received from another tab, clears the query cache and redirects to login.
 */
export const useCrossTabLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return; // BroadcastChannel not available — no-op
    }

    let channel: BroadcastChannel;

    try {
      channel = new BroadcastChannel(LOGOUT_BROADCAST_CHANNEL);

      channel.onmessage = (event: MessageEvent) => {
        if (event.data === 'logout') {
          queryClient.setQueryData(authQueryKeys.currentUser, null);
          queryClient.clear();
          navigate('/login', { replace: true });
        }
      };
    } catch {
      return; // Silently handle channel creation errors
    }

    return () => {
      try {
        channel.close();
      } catch {
        // Silently ignore close errors
      }
    };
  }, [navigate, queryClient]);
};
